import { Component, isDevMode, OnInit, ViewChild, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { TableSortService } from 'src/app/services/table-sort.service';
import { TableId } from 'src/app/constants/table.constant';
import { MatTableDataSource } from '@angular/material/table';
import { Subject, takeUntil } from 'rxjs';
import { Permission } from 'src/app/constants/permission.constant';
import { IOutputsCollection } from 'src/app/models/outputsCollection';
import { IPreference, IUserProfile } from 'src/app/models/users/userProfile';
import { AuthService } from 'src/app/services/auth.service';
import { OutputsCollectionsService } from 'src/app/services/outputs-collections.service';
import { OutputsCollectionDeleteResult } from 'src/app/models/outputs-collections/outputs-collections-result';
import { DeleteModalComponent } from '../../../shared/delete-modal/delete-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ClipboardService } from 'src/app/services/clipboard.service';

const SORTABLE_COLUMNS = {
  organizationName: 'string',
  jobNumber: 'string',
  jobName: 'string',
  createdAt: 'date',
} as const;

type SortableColumn = keyof typeof SORTABLE_COLUMNS;

@Component({
    selector: 'app-output-collections-list',
    templateUrl: './output-collections-list.component.html',
    styleUrl: './output-collections-list.component.scss',
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class OutputCollectionsListComponent implements OnInit, AfterViewInit {
  private destroy$ = new Subject();
  Permission = Permission;

  constructor(
    private outputsCollectionsService: OutputsCollectionsService,
    public authService: AuthService,
    public dialog: MatDialog,
    public alert: MatSnackBar,
    public clipboard: ClipboardService,
    private tableSortService: TableSortService,
  ) {}

  user!: IUserProfile;
  currentConfigDate!: IPreference;
  collections: IOutputsCollection[] = [];
  displayedColumns: string[] = [
    'jobNumber',
    'jobName',
    'description',
    'createdAt',
    'status',
    'Actions',
  ];
  dataSource = new MatTableDataSource<IOutputsCollection>();
  @ViewChild(MatPaginator)
  paginator!: MatPaginator;
  @ViewChild(MatSort)
  sort!: MatSort;

  public applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toUpperCase();
  }

  ngOnInit() {
    if (this.authService.hasPermission(Permission.OrganizationsRead)) {
      this.displayedColumns.splice(0, 0, 'organizationName');
    }
    this.listOutputsCollection();
    this.authService.currentPreference.subscribe((res) => {
      this.currentConfigDate = res;
    });
    this.authService
      .getUserInformation()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (profile: IUserProfile) => {
          this.user = profile;
        },
      });
  }

  ngAfterViewInit() {
    this.setupDataSourceSorting();
  }

  listOutputsCollection() {
    this.outputsCollectionsService.listOutputsCollection().subscribe((collections: IOutputsCollection[]) => {
      this.collections = collections;
      this.dataSource = new MatTableDataSource<IOutputsCollection>(this.collections);
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
      this.tableSortService.applySortToMatSort(this.sort, TableId.OUTPUT_COLLECTIONS_LIST);
      this.setupDataSourceSorting();
    });
  }

  private setupDataSourceSorting() {
    this.dataSource.sortingDataAccessor = (item, property) => {
      const value = item[property as keyof IOutputsCollection];
      const columnType = SORTABLE_COLUMNS[property as SortableColumn];

      if (columnType === 'date') {
        return new Date(value as string).getTime();
      }

      return (value as string)?.toString().toLowerCase() ?? '';
    };
  }

  downloadCollection(downloadUrl: string, id: string) {
    this.outputsCollectionsService.downloadOutputsCollection(id).subscribe(() => {
      if (isDevMode()) {
        console.log('[DEV] [OutputCollectionsListComponent] downloading collection', id);
      }
    });
    window.open(downloadUrl, '_blank');
  }

  deleteOutputCollection(collection: IOutputsCollection) {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      data: {
        header: `Delete Collection`,
        message: 'Are you sure you want to delete this collection?',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.outputsCollectionsService
            .deleteOutputsCollection(collection.id!)
            .subscribe((result: OutputsCollectionDeleteResult) => {
              this.alert.open('Successfully Deleted', 'close', {
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
                duration: 2000,
                panelClass: ['success'],
              });
              this.listOutputsCollection();
            });
        }
      });
  }

  onSortChange(sort: Sort): void {
    this.tableSortService.setSortState(TableId.OUTPUT_COLLECTIONS_LIST, sort);
  }

  setPageSizePreference(event: any) {
    if (event.pageSize != this.currentConfigDate.pageSize) {
      this.currentConfigDate.pageSize = event.pageSize;
      let payload = {
        name: this.user!.name,
        preference: this.currentConfigDate,
      };
      this.authService.updatePreference(payload).subscribe((res) => {
        if (isDevMode()) {
          console.log('[DEV] [OutputCollectionsListComponent] preference updated', res);
        }
      });
    }
  }

  copyLink(link: string) {
    this.clipboard.copyUrlAndTextToClipboard(link, 'Link');
  }
}
